import type { ReactNode } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * The one full-page "this did not work" card.
 *
 * BEN-141 QF4. Two failures replace the whole app rather than part of a page —
 * the sign-in SDK never loading, and a render that threw — and both used to
 * render either nothing or a bare sentence with nothing to do next. They share
 * a surface here so they read as the same product, and so neither can drift
 * back into a blank screen without the other noticing.
 */
export function FullPageNotice({
  title,
  children,
  actionLabel,
  onAction,
  pending = false,
}: {
  title: string;
  children: ReactNode;
  actionLabel: string;
  onAction: () => void;
  /** FP-170: still waiting, not failed. A spinner and a polite status, not an alert. */
  pending?: boolean;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div
        role={pending ? "status" : "alert"}
        className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-6 text-center"
      >
        {pending ? (
          <span className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
          </span>
        ) : (
          <span className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-warning/10">
            <AlertTriangle className="h-5 w-5 text-warning" aria-hidden="true" />
          </span>
        )}
        <h1 className="text-base font-semibold text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{children}</p>
        <Button className="mt-5" variant="outline" onClick={onAction}>
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}

/**
 * The root error boundary's fallback.
 *
 * It used to be `<p>Something went wrong.</p>` — 21 characters, no heading, no
 * way forward, and the whole app underneath it gone. Measured on the deployed
 * dev host with the app's own endpoints answering with an unexpected body: a
 * shape nothing could read threw inside a page, and this is every pixel the
 * user was left with. A blank-looking screen after a crash is the state the QA
 * sweep grades `state.blank`, and it is the state a user reports as "the app
 * broke" with nothing anyone can act on.
 */
export function AppErrorFallback() {
  return (
    <FullPageNotice
      title="This page ran into a problem"
      actionLabel="Reload the page"
      onAction={() => window.location.reload()}
    >
      Something went wrong while showing this page, so we stopped here rather than show you numbers
      that might be wrong. Reloading usually clears it. If it keeps happening, tell us through Help
      and support and we will look at it.
    </FullPageNotice>
  );
}
